import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Copy, Check } from 'lucide-react';
import mermaid from 'mermaid';
// TEMPORARY — remove after Sentry is verified receiving events
import { Sentry as SentryClient, isSentryInitialized } from '../lib/sentry';

export const WesSandbox: React.FC = () => {
  const diagramRef = useRef<HTMLDivElement>(null);
  const [copiedText, setCopiedText] = React.useState<string | null>(null);

  // Mermaid diagram definition for simple Preproduction → Production flow
  const mermaidDiagram = `
    graph LR
        PP["Preproduction"] --> PR["Production"]
        
        %% Optional: style for clarity
        classDef phase fill:#eef2ff,stroke:#3730a3,stroke-width:2px,color:#111827
        class PP,PR phase
  `;

  useEffect(() => {
    if (diagramRef.current) {
      mermaid.initialize({
        startOnLoad: true,
        theme: 'default',
        securityLevel: 'loose',
        flowchart: {
          useMaxWidth: true,
          htmlLabels: true
        }
      });

      // Clear previous content
      diagramRef.current.innerHTML = '';
      
      // Render the diagram
      mermaid.render('process-flow', mermaidDiagram).then(({ svg }) => {
        if (diagramRef.current) {
          diagramRef.current.innerHTML = svg;
        }
      }).catch((error) => {
        console.error('Mermaid rendering error:', error);
        if (diagramRef.current) {
          diagramRef.current.innerHTML = '<p>Error rendering diagram. Please try again.</p>';
        }
      });
    }
  }, []);

  const copyDiagramCode = () => {
    navigator.clipboard.writeText(mermaidDiagram);
    setCopiedText('Diagram code copied!');
    setTimeout(() => setCopiedText(null), 2000);
  };

  const downloadDiagram = () => {
    if (diagramRef.current) {
      const svg = diagramRef.current.querySelector('svg');
      if (svg) {
        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx?.drawImage(img, 0, 0);
          
          const link = document.createElement('a');
          link.download = 'process-flow-diagram.png';
          link.href = canvas.toDataURL('image/png');
          link.click();
        };
        
        img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-raised)]">
      {/* Header */}
      <div className="bg-gradient-to-r from-consulting-slate-blue-600 to-consulting-slate-blue-700 text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/elevation/home"
                className="flex items-center space-x-2 text-white hover:text-consulting-slate-blue-200 transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
                <span>Back to Registry</span>
              </Link>
            </div>
            <div className="text-right">
              <h1 className="text-3xl font-bold">Wes's Sandbox</h1>
              <p className="text-consulting-slate-blue-100 mt-2">Conceptual thinking space for process visualization</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* TEMPORARY: Sentry verification — remove after the test event shows
              up in the Sentry UI with workspace_id tag and user.id set. */}
          <div className="mb-8 border border-[var(--mustard)] bg-[var(--mustard-dim)] p-4">
            <p className="mb-2 text-sm font-semibold text-[var(--mustard)]">
              Sentry verification (temporary)
            </p>
            <p className="mb-3 text-sm text-[var(--mustard)]">
              Sentry initialized: {isSentryInitialized() ? 'yes' : 'no (check VITE_SENTRY_DSN)'}
            </p>
            <button
              type="button"
              onClick={() => {
                SentryClient.captureException(
                  new Error('Sentry test — Priorities.ai productization verification')
                );
              }}
              className="bg-[var(--mustard)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--mustard)]"
            >
              Test Sentry
            </button>
          </div>
          {/* Introduction */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-4">Process Map: Preproduction → Production</h2>
            <p className="text-charcoal-700 mb-6">
              A minimal, high-level view of our work pipeline. Use this to quickly spot where you are and what comes next.
            </p>
            <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-4 mb-6">
              <h3 className="font-semibold text-[var(--mustard)] mb-2">How to Use This Diagram</h3>
              <ul className="text-[var(--mustard)] space-y-1">
                <li>• Look at the map and ask: "Which side am I on?"</li>
                <li>• Identify the minimum next action to move forward</li>
                <li>• Confirm preproduction is complete before moving to production</li>
              </ul>
            </div>
          </div>

          {/* Diagram Controls */}
          <div className="flex flex-wrap gap-3 mb-6">
            <button
              onClick={copyDiagramCode}
              className="flex items-center space-x-2 px-4 py-2 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 hover:bg-consulting-slate-blue-200 transition-colors"
            >
              {copiedText ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              <span>{copiedText || 'Copy Diagram Code'}</span>
            </button>
            <button
              onClick={downloadDiagram}
              className="flex items-center space-x-2 px-4 py-2 bg-sage-100 text-sage-800 hover:bg-sage-200 transition-colors"
            >
              <Download className="h-4 w-4" />
              <span>Download PNG</span>
            </button>
          </div>

          {/* Mermaid Diagram */}
          <div className="bg-[var(--bg-raised)] border border-charcoal-200 p-6 mb-8">
            <div ref={diagramRef} className="mermaid-diagram-container"></div>
          </div>

          {/* Phase Summaries */}
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-[var(--surface)] border border-[var(--rule)] p-4">
              <h3 className="text-xl font-semibold text-charcoal-900 mb-2">Preproduction</h3>
              <p className="text-charcoal-600 text-sm">Everything that happens before production: framing, design, asset prep, environment setup, sequencing.</p>
            </div>
            <div className="bg-[var(--surface)] border border-[var(--rule)] p-4">
              <h3 className="text-xl font-semibold text-charcoal-900 mb-2">Production</h3>
              <p className="text-charcoal-600 text-sm">Execution and delivery: build, assemble, publish, present, ship.</p>
            </div>
          </div>

          {/* Areas of Investment */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-charcoal-900 mb-2">Areas of Investment</h3>
            <p className="text-charcoal-700 mb-4">Draft categories to focus effort; we can add specifics later.</p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-[var(--bg-raised)] border border-charcoal-200 p-4">
                <div className="mb-2">
                  <h4 className="font-semibold text-charcoal-800">Tools</h4>
                  <p className="text-charcoal-600 text-sm">Improve understanding, utilization, and value extraction.</p>
                </div>
                <div className="bg-[var(--surface)] border border-[var(--rule)] p-3">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-charcoal-800">Cursor</span>
                    <span className="text-xs text-charcoal-500">tool</span>
                  </div>
                  <ul className="text-charcoal-600 text-sm list-disc pl-5 mt-2 space-y-1">
                    <li>Map key workflows (search, refactor, multi-file edits)</li>
                    <li>Develop prompt patterns and code edit recipes</li>
                    <li>Shortcut hygiene and keyboard-driven flow</li>
                    <li>Project setup templates and reusable snippets</li>
                  </ul>
                </div>
              </div>
              <div className="bg-[var(--bg-raised)] border border-charcoal-200 p-4">
                <div className="mb-2">
                  <h4 className="font-semibold text-charcoal-800">Agent Research Projects</h4>
                  <p className="text-charcoal-600 text-sm">Ideas to explore and evaluate.</p>
                </div>
                <ul className="text-charcoal-600 text-sm list-disc pl-5 space-y-1">
                  <li>Research VS Code Extensions</li>
                  <li>Research and compile expert-usage docs per tool to teach Cursor how top experts use it; enable on-demand application and explanations</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Usage Tips */}
          <div className="bg-charcoal-50 border border-charcoal-200 p-6">
            <h3 className="text-lg font-semibold text-charcoal-900 mb-4">💡 Usage Tips</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-charcoal-800 mb-2">For Personal Use</h4>
                <ul className="text-charcoal-600 text-sm space-y-1">
                  <li>• Start by asking "Which side am I on?"</li>
                  <li>• Name the smallest next move to advance</li>
                  <li>• Avoid entering production before preproduction is complete</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-charcoal-800 mb-2">For Team Use</h4>
                <ul className="text-charcoal-600 text-sm space-y-1">
                  <li>• Align on what “ready for production” means</li>
                  <li>• Use the diagram in standups to track phase</li>
                  <li>• Customize the boxes with your artifacts and gates</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
